import { useState } from 'react'
import {
    ArrowRight, Check, Hash, KeyRound, Link2, LogIn,
    LogOut, Lock, Pencil, Plus, Sparkles, Trash2, Tv, X
} from 'lucide-react'
import { MriButton, MriInput, cn } from '@mriqbox/ui-kit'
import { roomLinkFor } from '@/lib/format'
import { Avatar } from '@/components/Avatar'
import { getSavedPin } from '@/lib/savedPins'
import type { PermanentRoom } from '@/hooks/usePermanentRooms'
import { RoomEditor, type RoomEditorValues } from '@/components/RoomEditor'

export interface PermanentRoomsProps {
    loading: boolean
    available: boolean
    user: { id: string; username: string } | null
    rooms: PermanentRoom[]
    error: string | null
    currentNickname?: string
    /** Foto de perfil de quem esta logado — a mesma que vai pra sala. */
    avatar?: string | null
    onSelectRoom?: (code: string) => void
    onJoinRoom?: (code: string, pin?: string) => void
    onRequestPin?: (code: string, name?: string) => void
    onCreate: (name: string, pin: string) => void
    /**
     * Salva a edicao. `pin` ausente mantem o que ja existe, string vazia
     * remove a tranca e uma string troca — a mesma convencao da rota.
     */
    onUpdate: (code: string, fields: RoomEditorValues) => void
    onDelete: (code: string) => void
}

export function PermanentRooms({
    loading,
    available,
    user,
    rooms,
    error,
    currentNickname = '',
    avatar,
    onSelectRoom,
    onJoinRoom,
    onRequestPin,
    onCreate,
    onUpdate,
    onDelete
}: PermanentRoomsProps) {
    const [name, setName] = useState('')
    const [usePin, setUsePin] = useState(false)
    const [pin, setPin] = useState('')
    const [creating, setCreating] = useState(false)
    const [copiedCode, setCopiedCode] = useState<string | null>(null)
    const [pinPromptRoom, setPinPromptRoom] = useState<PermanentRoom | null>(null)
    const [pinPromptValue, setPinPromptValue] = useState('')
    const [confirmDeleteCode, setConfirmDeleteCode] = useState<string | null>(null)
    const [editingCode, setEditingCode] = useState<string | null>(null)

    if (loading || !available) return null

    // Caso o usuário ainda não esteja logado
    if (!user) {
        return (
            <div className="mt-5 overflow-hidden rounded-2xl border border-border/80 bg-card/60 p-4.5 shadow-md backdrop-blur-sm">
                <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary border border-primary/20">
                        <Link2 className="h-5 w-5" />
                    </div>
                    <div>
                        <h3 className="text-sm font-bold text-foreground flex items-center gap-1.5">
                            Salas com Link Fixo
                            <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">
                                Permanente
                            </span>
                        </h3>
                        <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                            Crie endereços permanentes que nunca mudam para reuniões recorrentes ou canais fixos.
                        </p>
                    </div>
                </div>
                <MriButton variant="outline" className="mt-4 w-full justify-center gap-2 border-primary/30 hover:bg-primary/10 hover:border-primary/50" asChild>
                    <a href="/api/auth/discord">
                        <LogIn className="h-4 w-4 text-[#5865F2]" />
                        Entrar com Discord
                    </a>
                </MriButton>
            </div>
        )
    }

    const handleCopy = (code: string, e: React.MouseEvent) => {
        e.stopPropagation()
        void navigator.clipboard.writeText(roomLinkFor(code))
        setCopiedCode(code)
        setTimeout(() => setCopiedCode(null), 2000)
    }

    const handleRoomClick = (room: PermanentRoom) => {
        onSelectRoom?.(room.code)
        const saved = getSavedPin(room.code)
        if (saved) {
            onJoinRoom?.(room.code, saved)
            return
        }
        if (room.requiresPin) {
            if (onRequestPin) {
                onRequestPin(room.code, room.name)
            } else {
                setPinPromptRoom(room)
                setPinPromptValue('')
            }
            return
        }
        onJoinRoom?.(room.code)
    }

    const handlePinPromptSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (!pinPromptRoom || !pinPromptValue.trim()) return
        onSelectRoom?.(pinPromptRoom.code)
        onJoinRoom?.(pinPromptRoom.code, pinPromptValue.trim())
        setPinPromptRoom(null)
        setPinPromptValue('')
    }

    return (
        <div className="mt-4 space-y-3">
            {/* Header com estilo de Perfil Discord */}
            <div className="flex items-center justify-between rounded-xl bg-background/50 border border-border/60 px-3 py-2">
                <div className="flex items-center gap-2.5">
                    {/*
                      * A mesma foto que aparece na sala. Antes este bloco
                      * montava as iniciais na mao e ignorava o avatar, entao a
                      * pessoa se via com foto logo acima e sem foto aqui.
                      */}
                    <Avatar nickname={user.username} avatar={avatar} size={28} />
                    <div>
                        <div className="flex items-center gap-1.5">
                            <span className="text-xs font-bold text-foreground leading-none">{user.username}</span>
                            <span className="rounded bg-muted px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground">
                                {rooms.length}/10 salas
                            </span>
                        </div>
                        <span className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5">
                            <Sparkles className="h-2.5 w-2.5 text-primary" /> Salas Fixas
                        </span>
                    </div>
                </div>

                <a
                    href="/api/auth/logout"
                    title="Desconectar conta"
                    className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                >
                    <LogOut className="h-3.5 w-3.5" />
                </a>
            </div>

            {/* Modal/Prompt Inline de PIN quando clica em sala protegida */}
            {pinPromptRoom && (
                <form
                    onSubmit={handlePinPromptSubmit}
                    className="mt-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 animate-in fade-in slide-in-from-top-1 duration-200"
                >
                    <div className="flex items-center justify-between text-xs font-semibold text-amber-300">
                        <span className="flex items-center gap-1.5">
                            <KeyRound className="h-3.5 w-3.5 text-amber-400" />
                            PIN para {pinPromptRoom.name}
                        </span>
                        <button
                            type="button"
                            onClick={() => setPinPromptRoom(null)}
                            className="text-amber-400/80 hover:text-amber-300"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    </div>
                    <div className="mt-2 flex gap-2">
                        <MriInput
                            value={pinPromptValue}
                            maxLength={12}
                            autoFocus
                            placeholder="Digite o PIN da sala"
                            className="h-8 text-xs bg-black/40 border-amber-500/30 text-amber-100"
                            onChange={(e) => setPinPromptValue(e.target.value)}
                        />
                        <MriButton
                            type="submit"
                            size="sm"
                            className="h-8 shrink-0 bg-amber-500 text-black hover:bg-amber-400"
                            disabled={!pinPromptValue.trim()}
                        >
                            Entrar
                        </MriButton>
                    </div>
                </form>
            )}

            {/* Lista de Canais / Salas com estilo Discord */}
            {rooms.length > 0 && (
                <div className="mt-3 space-y-1.5">
                    {rooms.map((room) => {
                        const isCopied = copiedCode === room.code
                        const isConfirming = confirmDeleteCode === room.code
                        const isEditing = editingCode === room.code

                        if (isEditing) {
                            return (
                                <RoomEditor
                                    key={room.code}
                                    room={room}
                                    onCancel={() => setEditingCode(null)}
                                    onSave={(fields) => {
                                        onUpdate(room.code, fields)
                                        setEditingCode(null)
                                    }}
                                />
                            )
                        }

                        return (
                            <div
                                key={room.code}
                                onClick={() => handleRoomClick(room)}
                                className={cn(
                                    'group relative flex items-center justify-between rounded-xl border border-border/50 bg-background/50 p-2.5 transition-all cursor-pointer select-none',
                                    'hover:border-primary/50 hover:bg-primary/5 hover:shadow-sm'
                                )}
                            >
                                <div className="flex items-center gap-2.5 min-w-0 flex-1 pr-2">
                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-card border border-border/60 text-muted-foreground group-hover:text-primary group-hover:border-primary/40 transition-colors">
                                        {room.requiresPin ? (
                                            <Lock className="h-4 w-4 text-amber-400/90" />
                                        ) : (
                                            <Tv className="h-4 w-4" />
                                        )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1.5">
                                            <p className="truncate text-xs font-semibold text-foreground group-hover:text-primary transition-colors">
                                                {room.name}
                                            </p>
                                            {room.requiresPin && (
                                                <span className="rounded bg-amber-500/15 border border-amber-500/30 px-1 py-0.2 text-[9px] font-bold text-amber-400">
                                                    PIN
                                                </span>
                                            )}
                                        </div>
                                        <p className="font-mono text-[10px] tracking-wider text-muted-foreground/80 mt-0.5">
                                            {room.code}
                                        </p>
                                    </div>
                                </div>

                                {/*
                                  * Sempre visiveis. Editar e excluir viviam em
                                  * `opacity-0 group-hover`, entao a unica porta
                                  * pra trocar nome e PIN so existia se a pessoa
                                  * parasse o mouse no card — e em toque, onde
                                  * nao ha hover, nao existia de jeito nenhum.
                                  */}
                                <div className="flex items-center gap-1 shrink-0">
                                    <button
                                        type="button"
                                        title={isCopied ? 'Link copiado!' : 'Copiar link permanente'}
                                        onClick={(e) => handleCopy(room.code, e)}
                                        className={cn(
                                            'flex h-7.5 w-7.5 items-center justify-center rounded-lg border text-xs transition-colors',
                                            isCopied
                                                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400'
                                                : 'border-border/60 bg-card text-muted-foreground hover:bg-muted hover:text-foreground'
                                        )}
                                    >
                                        {isCopied ? <Check className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />}
                                    </button>

                                    <button
                                        type="button"
                                        title={`Editar ${room.name}`}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            setConfirmDeleteCode(null)
                                            setEditingCode(room.code)
                                        }}
                                        className="flex h-7.5 w-7.5 items-center justify-center rounded-lg border border-border/60 bg-card text-muted-foreground transition-colors hover:border-primary/30 hover:bg-primary/10 hover:text-primary"
                                    >
                                        <Pencil className="h-3.5 w-3.5" />
                                    </button>

                                    {isConfirming ? (
                                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                            <button
                                                type="button"
                                                title="Confirmar exclusão"
                                                onClick={() => {
                                                    onDelete(room.code)
                                                    setConfirmDeleteCode(null)
                                                }}
                                                className="flex h-7.5 px-2 items-center justify-center rounded-lg bg-destructive text-[10px] font-bold text-destructive-foreground hover:bg-destructive/90 transition-colors"
                                            >
                                                Excluir?
                                            </button>
                                            <button
                                                type="button"
                                                title="Cancelar"
                                                onClick={() => setConfirmDeleteCode(null)}
                                                className="flex h-7.5 w-7.5 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            title={`Excluir ${room.name}`}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                setConfirmDeleteCode(room.code)
                                            }}
                                            className="flex h-7.5 w-7.5 items-center justify-center rounded-lg border border-border/60 bg-card text-muted-foreground transition-colors hover:border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    )}

                                    <button
                                        type="button"
                                        title={currentNickname ? `Entrar como ${currentNickname}` : 'Entrar na sala'}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleRoomClick(room)
                                        }}
                                        className="flex h-7.5 items-center gap-1 rounded-lg bg-primary/10 border border-primary/30 px-2.5 text-xs font-semibold text-primary hover:bg-primary hover:text-primary-foreground transition-all"
                                    >
                                        Entrar
                                        <ArrowRight className="h-3 w-3" />
                                    </button>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}

            {/* Formulário de Criação no Estilo Discord Channel Creation */}
            {creating ? (
                <form
                    className="mt-3 space-y-3 rounded-xl border border-primary/30 bg-card/80 p-3.5 animate-in fade-in zoom-in-95 duration-150"
                    onSubmit={(event) => {
                        event.preventDefault()
                        if (!name.trim()) return
                        onCreate(name.trim(), usePin ? pin.trim() : '')
                        setName('')
                        setPin('')
                        setUsePin(false)
                        setCreating(false)
                    }}
                >
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-wider text-primary flex items-center gap-1.5">
                            <Plus className="h-3.5 w-3.5" /> Criar Sala Fixa
                        </span>
                        <button
                            type="button"
                            onClick={() => {
                                setCreating(false)
                                setName('')
                                setPin('')
                                setUsePin(false)
                            }}
                            className="text-muted-foreground hover:text-foreground"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    </div>

                    <div>
                        <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground block mb-1">
                            Nome da Sala
                        </label>
                        <div className="relative">
                            <span className="absolute inset-y-0 left-2.5 flex items-center text-muted-foreground">
                                <Hash className="h-3.5 w-3.5" />
                            </span>
                            <MriInput
                                value={name}
                                maxLength={60}
                                autoFocus
                                placeholder="ex.: daily-devs, sala-jogos"
                                className="pl-8 text-xs"
                                onChange={(event) => setName(event.target.value)}
                            />
                        </div>
                    </div>

                    {/* Toggle de Canal Privado com PIN */}
                    <div className="rounded-lg border border-border/70 bg-background/50 p-2.5">
                        <label className="flex items-center justify-between cursor-pointer">
                            <div className="flex items-center gap-2">
                                <KeyRound className={cn('h-4 w-4', usePin ? 'text-amber-400' : 'text-muted-foreground')} />
                                <div>
                                    <p className="text-xs font-semibold text-foreground">Proteger com PIN</p>
                                    <p className="text-[10px] text-muted-foreground">Exigir senha para quem for entrar</p>
                                </div>
                            </div>
                            <input
                                type="checkbox"
                                checked={usePin}
                                onChange={(e) => setUsePin(e.target.checked)}
                                className="h-4 w-4 rounded border-border accent-primary cursor-pointer"
                            />
                        </label>

                        {usePin && (
                            <div className="mt-2.5 pt-2.5 border-t border-border/60 animate-in fade-in slide-in-from-top-1 duration-150">
                                <MriInput
                                    value={pin}
                                    maxLength={12}
                                    placeholder="Defina o PIN (4 a 12 caracteres)"
                                    className="text-xs"
                                    onChange={(event) => setPin(event.target.value)}
                                />
                            </div>
                        )}
                    </div>

                    <div className="flex gap-2 pt-1">
                        <MriButton
                            type="submit"
                            size="sm"
                            className="flex-1 font-semibold"
                            disabled={!name.trim() || (usePin && (!pin.trim() || pin.length < 4))}
                        >
                            Criar Sala
                        </MriButton>
                        <MriButton
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setCreating(false)
                                setName('')
                                setPin('')
                                setUsePin(false)
                            }}
                        >
                            Cancelar
                        </MriButton>
                    </div>
                </form>
            ) : (
                <MriButton
                    variant="outline"
                    size="sm"
                    className={cn('w-full mt-3 justify-center gap-2 border-dashed border-border hover:border-primary/60 hover:bg-primary/5 hover:text-primary')}
                    onClick={() => setCreating(true)}
                >
                    <Plus className="h-3.5 w-3.5" />
                    Nova sala fixa
                </MriButton>
            )}

            {error && <p className="mt-2 text-xs text-destructive">{error}</p>}

            {rooms.length === 0 && !creating && (
                <div className="mt-3 text-center py-3 text-[11px] text-muted-foreground border border-dashed border-border/60 rounded-xl">
                    <p className="font-medium text-foreground/80">Nenhuma sala fixa ainda</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                        Crie uma sala permanente que dura 7 dias após o último acesso.
                    </p>
                </div>
            )}
        </div>
    )
}
